import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { InternalAuditEntityRegisterResponse } from '@risksmart-app/trpc/src/types';
import type { GetInternalAuditsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC internal audit entities data to match the GraphQL query structure
 */
export function mapTrpcInternalAuditEntitiesToGraphQL(
  trpcData: InternalAuditEntityRegisterResponse | undefined
): GetInternalAuditsQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    internal_audit_entity: trpcData.internal_audit_entity,
  };
}

export const useGetInternalAuditEntitiesRegister = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.internalAuditEntity.register.queryOptions(),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcInternalAuditEntitiesToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
